/**
 * File Manager Utility
 * Handles file upload tracking, removal, and download functionality
 */

/**
 * Check if a file is already uploaded (has a URL)
 * @param {File|string|null} file - File object or URL string
 * @returns {boolean} - True if file is already uploaded
 */
export const isFileUploaded = (file) => {
	return typeof file === 'string' && file.length > 0;
};

/**
 * Check if file is a new upload (File object)
 * @param {File|string|null} file - File object or URL string
 * @returns {boolean} - True if file is a new File object
 */
export const isNewFile = (file) => {
	return file instanceof File;
};

/**
 * Get file display name
 * @param {File|string|null} file - File object or URL string
 * @returns {string} - Display name for the file
 */
export const getFileDisplayName = (file) => {
	if (file instanceof File) {
		return file.name;
	}
	if (typeof file === 'string') {
		// Extract filename from URL
		try {
			const url = new URL(file);
			const pathname = url.pathname;
			const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
			return decodeURIComponent(filename) || 'Uploaded File';
		} catch (e) {
			return 'Uploaded File';
		}
	}
	return 'No File';
};

/**
 * Get file size display string
 * @param {File} file - File object
 * @returns {string} - Human readable file size
 */
export const getFileSizeDisplay = (file) => {
	if (!(file instanceof File)) return '';
	
	const bytes = file.size;
	if (bytes === 0) return '0 Bytes';
	
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Download file from URL
 * @param {string} url - File URL
 * @param {string} filename - Suggested filename for download
 */
export const downloadFile = async (url, filename = 'download.pdf') => {
	try {
		const response = await fetch(url);
		const blob = await response.blob();
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = downloadUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);
		return true;
	} catch (error) {
		console.error('Error downloading file:', error);
		return false;
	}
};

/**
 * Open file in new tab
 * @param {File|string} file - File object or URL string
 */
export const viewFile = (file) => {
	if (typeof file === 'string') {
		window.open(file, '_blank');
	} else if (file instanceof File) {
		const url = URL.createObjectURL(file);
		window.open(url, '_blank');
		// Clean up the URL after a delay
		setTimeout(() => URL.revokeObjectURL(url), 100);
	}
};

/**
 * Get file metadata
 * @param {File|string|null} file - File object or URL string
 * @returns {object} - File metadata
 */
export const getFileMetadata = (file) => {
	return {
		isUploaded: isFileUploaded(file),
		isNew: isNewFile(file),
		displayName: getFileDisplayName(file),
		size: file instanceof File ? getFileSizeDisplay(file) : null,
		canDownload: typeof file === 'string' || file instanceof File,
		canRemove: Boolean(file),
		url: typeof file === 'string' ? file : null,
	};
};

/**
 * Create a file state object for tracking uploads
 * @param {File|string|null} file - File object or URL string
 * @returns {object} - File state object
 */
export const createFileState = (file) => {
	if (!file) {
		return {
			file: null,
			uploaded: false,
			metadata: getFileMetadata(null),
		};
	}
	
	return {
		file: file,
		uploaded: isFileUploaded(file),
		metadata: getFileMetadata(file),
	};
};

/**
 * Determine if file should be uploaded (new File object that hasn't been uploaded)
 * @param {File|string|null} file - File object or URL string
 * @param {File|string|null} originalFile - Original file from loaded data
 * @returns {boolean} - True if file should be uploaded
 */
export const shouldUploadFile = (file, originalFile = null) => {
	// No file provided
	if (!file) return false;
	
	// File is already uploaded (URL string)
	if (isFileUploaded(file)) return false;
	
	// File is new (File object)
	if (isNewFile(file)) {
		// If we have an original file URL and current file is a File object,
		// check if it's actually a new upload or the same file
		if (originalFile && isFileUploaded(originalFile)) {
			// This is a new file being uploaded to replace the old one
			return true;
		}
		// This is a new file
		return true;
	}
	
	return false;
};

/**
 * Get file for FormData submission
 * Decides whether to send file, URL, or nothing
 * @param {File|string|null} file - File object or URL string
 * @param {File|string|null} originalFile - Original file from loaded data
 * @returns {object} - { type: 'file'|'url'|'none', value: File|string|null }
 */
export const getFileForSubmission = (file, originalFile = null) => {
	// No file provided
	if (!file) {
		// Check if we should preserve original
		if (originalFile && isFileUploaded(originalFile)) {
			return { type: 'url', value: originalFile };
		}
		return { type: 'none', value: null };
	}
	
	// File is already uploaded (URL string)
	if (isFileUploaded(file)) {
		return { type: 'url', value: file };
	}
	
	// File is new (File object)
	if (isNewFile(file)) {
		return { type: 'file', value: file };
	}
	
	return { type: 'none', value: null };
};
